/**
 * The one answer to "which Edge host holds this conversation" (agion/Agion#2485,
 * PLAN-20260929 edge-host-placement slice 2). A run's history and files live
 * on the host dispatch accepted it on, which the platform records as the
 * execution's edge_host_id. That host wins when a run is open; otherwise the
 * mission's own host. A host group id is never a host: a group-bound mission
 * with no recorded run host has none, and chat says so instead of guessing.
 * Before such a mission has a run, the platform's placement names the member
 * a new run will be placed on (slice 4), so files can go there first.
 */
import { useEffect, useState } from 'react'
import { executionsApi, missionsApi } from '../services/api'

export function resolveEdgeHost({
  runHostId,
  missionHostId,
  placementHostId,
}: {
  runHostId?: string | null
  missionHostId?: string | null
  placementHostId?: string | null
}): string | undefined {
  return runHostId || missionHostId || placementHostId || undefined
}

/** The placement read's state for a mission with no known host. */
export interface MissionPlacement {
  /** The member a new run will be placed on, once the platform names it. */
  hostId?: string
  /** Why the placement could not name a member: the thrown ChatApiError. */
  failure?: unknown
  /** The read is enabled and has not answered yet. */
  pending: boolean
}

/**
 * The host the platform would place a new run of the mission on, read only
 * when enabled (no run host and no mission host is known). A follow-up names
 * the run it continues. A failed read keeps its failure, so chat can say why
 * a group-bound mission cannot take files now (agion/Agion#2485).
 */
export function useMissionPlacement(
  missionId: string | undefined,
  enabled: boolean,
  previousExecutionId?: string | null,
): MissionPlacement {
  const key = `${missionId ?? ''}|${previousExecutionId ?? ''}`
  const [placed, setPlaced] = useState<{ key: string; hostId?: string; failure?: unknown }>()

  useEffect(() => {
    if (!enabled || !missionId) return
    let cancelled = false
    const read = async () => {
      try {
        const placement = await missionsApi.getPlacement(missionId, previousExecutionId ?? undefined)
        if (!cancelled) setPlaced({ key, hostId: placement.hostId || undefined })
      } catch (error) {
        if (cancelled) return
        console.warn('[useMissionPlacement] could not read the placement', error)
        setPlaced({ key, failure: error })
      }
    }
    void read()
    return () => {
      cancelled = true
    }
  }, [enabled, missionId, previousExecutionId, key])

  if (!enabled || !missionId) return { pending: false }
  if (placed?.key !== key) return { pending: true }
  return { hostId: placed.hostId, failure: placed.failure, pending: false }
}

// Dispatch records the host just after the run is sent, so a run launched in
// this session may not carry it on the first read.
const LIVE_RETRIES = 4
const LIVE_RETRY_MS = 1500

/**
 * The recorded Edge host of the open run, read from the execution. Undefined
 * while unknown. A live run is re-read a few times until dispatch has
 * recorded it.
 */
export function useRunEdgeHost(executionId: string | null | undefined, live: boolean): string | undefined {
  const [host, setHost] = useState<{ executionId: string; hostId?: string }>()

  useEffect(() => {
    if (!executionId) return
    let cancelled = false
    let attempt = 0
    let timer: ReturnType<typeof setTimeout> | undefined

    const read = async () => {
      attempt += 1
      try {
        const execution = await executionsApi.getExecution(executionId)
        if (cancelled) return
        const hostId = execution.edge_host_id || undefined
        setHost({ executionId, hostId })
        if (!hostId && live && attempt < LIVE_RETRIES) timer = setTimeout(() => void read(), LIVE_RETRY_MS)
      } catch (error) {
        if (!cancelled) console.warn('[useRunEdgeHost] could not read the run host', error)
      }
    }
    void read()
    return () => {
      cancelled = true
      if (timer) clearTimeout(timer)
    }
  }, [executionId, live])

  return host && host.executionId === executionId ? host.hostId : undefined
}
